import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/layout/PageHeader';
import { useToast } from '../contexts/ToastContext';
import {
  consentApi,
  type PreferenceAction,
  type PreferenceCategory,
  type PreferenceState,
} from '../services/api';
import { applyPreferenceState } from '../lib/analyticsConsent';

/**
 * Email & Privacy — the preference center (task 461).
 *
 * Three opt-in/opt-out toggles backed by the AMS preference ledger, read and
 * written through the innovate-service proxy (`/api/consent/preferences`).
 * Every toggle renders RECORDED truth: the state shown always comes from a
 * server response (the GET, or the current-state map the PUT returns), never
 * from an optimistic flip — a compliance toggle must show what is actually on
 * file. Failures surface a retryable error and leave the previous state
 * visible.
 *
 * The `analytics_tracking` choice is applied to the OpenPanel client live via
 * `applyPreferenceState` — opting out silences identify/custom events/screen
 * views in-session; opting back in resumes them.
 */

const PREFERENCE_ROWS: Array<{
  category: PreferenceCategory;
  title: string;
  description: string;
}> = [
  {
    category: 'marketing_email',
    title: 'Marketing email',
    description:
      'Lifecycle and promotional email campaigns about Monarch Works offers.',
  },
  {
    category: 'product_updates',
    title: 'Product updates',
    description: 'Feature announcements, changelog highlights, and the newsletter.',
  },
  {
    category: 'analytics_tracking',
    title: 'Analytics',
    description:
      'First-party product analytics tied to your account that helps us understand how the product is used. On by default; opting out stops analytics events from this browser immediately.',
  },
];

function PreferenceToggle({
  checked,
  disabled,
  label,
  onToggle,
  testId,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:opacity-50 ${
        checked ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

export default function EmailPrivacyPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pendingCategory, setPendingCategory] = useState<PreferenceCategory | null>(null);

  const {
    data: preferences,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['consent', 'preferences'],
    queryFn: () => consentApi.getPreferences().then((res) => res.data.data),
    retry: 1,
  });

  // Keep the session cache + analytics gate aligned with recorded truth
  // whenever fresh server state arrives.
  useEffect(() => {
    if (preferences) applyPreferenceState(preferences);
  }, [preferences]);

  const updatePreference = useMutation({
    mutationFn: ({
      category,
      action,
    }: {
      category: PreferenceCategory;
      action: PreferenceAction;
    }) => consentApi.updatePreference(category, action).then((res) => res.data.data),
    onMutate: ({ category }) => setPendingCategory(category),
    onSuccess: (state: PreferenceState) => {
      // Render the state the server recorded (and flip the analytics gate
      // live) — never an optimistic value.
      queryClient.setQueryData(['consent', 'preferences'], state);
      applyPreferenceState(state);
    },
    onError: () => {
      toast.error(
        'Preference not saved',
        'Your choice could not be recorded. Please try again.'
      );
    },
    onSettled: () => setPendingCategory(null),
  });

  const handleToggle = (category: PreferenceCategory) => {
    if (!preferences || updatePreference.isPending) return;
    const action: PreferenceAction =
      preferences[category] === 'opt_in' ? 'opt_out' : 'opt_in';
    updatePreference.mutate({ category, action });
  };

  return (
    <div>
      <PageHeader
        title="Email & Privacy"
        description="Choose which email you receive from us and whether product analytics may be collected. You can change these at any time."
      />

      <div className="mt-6 max-w-2xl">
        {isLoading && (
          <p
            className="text-sm text-gray-500 dark:text-gray-400"
            data-testid="email-privacy-loading"
          >
            Loading your preferences…
          </p>
        )}

        {isError && !isLoading && (
          <div data-testid="email-privacy-error">
            <p className="text-sm text-red-600 dark:text-red-400">
              Your preferences are unavailable right now. Please try again.
            </p>
            <button
              onClick={() => refetch()}
              data-testid="email-privacy-retry"
              className="mt-2 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {preferences && (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {PREFERENCE_ROWS.map(({ category, title, description }) => (
              <li key={category} className="flex items-start justify-between gap-6 p-4">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
                </div>
                <PreferenceToggle
                  checked={preferences[category] === 'opt_in'}
                  disabled={pendingCategory !== null}
                  label={title}
                  onToggle={() => handleToggle(category)}
                  testId={`email-privacy-toggle-${category}`}
                />
              </li>
            ))}
          </ul>
        )}

        <p
          className="mt-4 text-sm text-gray-500 dark:text-gray-400"
          data-testid="email-privacy-transactional-note"
        >
          Transactional email — receipts, security notices, and password resets — is
          always sent and cannot be disabled.
        </p>
      </div>
    </div>
  );
}
